import { useSubClient } from 'graphql/client';
import { useEffect, useState } from 'react';
import {
  BoxStatusFragment,
  HomeBoxStatusDocument,
  HomeBoxStatusSubscription,
  Maybe,
  Unlock_Request_Type_Enum,
} from 'generated/graphql';
import { queryCache } from 'react-query';
import { ExecutionResult } from 'graphql';
import { Observable } from 'subscriptions-transport-ws';
import { format, isEqual, parseISO } from 'date-fns';
import { ja } from 'date-fns/locale';
import { useStatusSnack } from './useStatusSnack';

export type BoxStatus = {
  boxId: number;
  updatedAt: Date | null;
  updatedAtText: string;
  isOnline: boolean;
  isBattery: boolean | null;
  isOpen: boolean | null;
  isUnlocked: boolean | null;
  requestStatus: Unlock_Request_Type_Enum | null;
};

export const getDateTxt = (updatedAt: Date) =>
  format(updatedAt, 'yy/MM/dd HH:mm:ss', { locale: ja });

type StatusesSub = Observable<ExecutionResult<HomeBoxStatusSubscription>>;

const useSubBoxStatuses = ({ enabled }: { enabled: boolean }) => {
  const subClient = useSubClient();
  const displaySnack = useStatusSnack();

  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (enabled) {
      const parseStatus = (status: BoxStatusFragment, updatedAt: Date) => {
        const data: BoxStatus = {
          boxId: status.box_id,
          updatedAt,
          updatedAtText: getDateTxt(updatedAt),
          isOnline: status.is_online,
          isBattery: status.is_backup_battery ?? null,
          isOpen: status.is_open ?? null,
          isUnlocked: status.is_unlocked ?? null,
          requestStatus: status.request_status ?? null,
        };
        return data;
      };

      const onStatusData = (
        data: Maybe<HomeBoxStatusSubscription> | undefined
      ) => {
        if (data) {
          data.box_status.forEach((s) => {
            const key = ['status', s.box_id];
            const prevData = queryCache.getQueryData<BoxStatus>(key);

            displaySnack(prevData, s);

            const prevUpdatedAt = prevData?.updatedAt;
            const newUpdatedAt = parseISO(s.updated_at);

            if (!prevUpdatedAt || !isEqual(prevUpdatedAt, newUpdatedAt)) {
              const data = parseStatus(s, newUpdatedAt);
              queryCache.setQueryData(key, data, { staleTime: Infinity });
            }
          });
        }
        setIsLoading(false);
      };

      const statuses: StatusesSub = subClient.request({
        query: HomeBoxStatusDocument,
      });

      const { unsubscribe } = statuses.subscribe({
        next: ({ data }) => onStatusData(data),
      });

      return () => unsubscribe();
    }
  }, [enabled, subClient, displaySnack]);

  return isLoading;
};

export default useSubBoxStatuses;
